import { useCallback } from "react"

import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Icon } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import actions from "actions"
import api from "api"
import { useAppDispatch, useAppSelector } from "store"
import util from "utils/utils"

import BaseChip, { type ChipConfiguration } from "./BaseChip"
import { SocialLinks } from "./SocialLinks"

type IUserChipBase = {
  user: Partial<OpenAPI.Schemas["User"]>
  showSocials?: boolean
}

type IUserChip = IUserChipBase & ChipConfiguration

function UserChip({ user, actionIcon, actionClick, actionTooltip, showSocials, ...props }: IUserChip) {
  const dispatch = useAppDispatch()

  const navigate = useNavigate()
  const currentUser = useAppSelector((state) => state.user)

  const params = useParams()
  const matchId = params?.id

  const hasProfile = user?.profile
  if (!hasProfile) {
    user = {
      profile: {
        fullName: "Unknown User",
      },
    }
  }

  const { jobTitle = "", department = "" } = user.profile
  let description = jobTitle
  if (department) {
    if (jobTitle) {
      description += " - "
    }
    description += department
  }

  const userCanListUsers =
    util.hasPermission(currentUser, "org.listUsers", currentUser?.ownerOrganisation?._id) ||
    util.hasPermission(currentUser, "group.listUsers", matchId)

  const messageUser = useCallback(
    (member) => {
      api.messages.findThreadByUser(
        member._id,
        (data) => {
          dispatch(
            actions.messages.selectThread(
              data?.thread || { _id: `newThread:${member._id}`, participantUsers: [member] },
            ),
          )
        },
        (err) => toast.error(err.message),
      )
    },
    [dispatch],
  )

  const canMessage =
    user._id !== currentUser?._id && currentUser?.ownerOrganisation?.enabledFeatures.includes("directMessaging")

  const ghost = useCallback(() => {
    util
      .confirm(
        "Really ghost as this user?",
        "Ghosting allows you to view the platform and carry out actions as though you are the user. You can un-ghost at any time by tapping the avatar at the top-right of the screen.",
      )
      .then(() =>
        api.users.ghost(
          user?._id,
          () => {
            dispatch(actions.user.ghost(user?._id))
            navigate("/")
          },
          (err) => toast.error(err.message),
        ),
      )
      .catch(() => {})
  }, [navigate, user?._id, dispatch])

  const canGhost =
    user?._id &&
    !(user?._id === currentUser?._id) &&
    util.hasPermission(currentUser, "super.ghostUser", currentUser?.ownerOrganisation)

  return (
    <BaseChip
      {...props}
      title={
        <>
          {user.profile.fullName}
          {showSocials ? <SocialLinks user={user} /> : null}
        </>
      }
      largeTitle={
        <>
          {user.profile.fullName}
          <SocialLinks user={user} />
        </>
      }
      subtitle={description}
      content={
        <>
          <p style={{ color: "grey", fontSize: 13, marginBottom: 8, marginTop: 0 }}>{user?.profile?.tagline}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", marginTop: 10 }}>
            {canMessage ? (
              <Button
                size="small"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  messageUser(user)
                }}
              >
                <Icon name="mail outline" />
                Message
              </Button>
            ) : null}
            {userCanListUsers ? (
              <Button size="small" onClick={() => navigate(`/users/${user?._id}`)}>
                <Icon name="user outline" />
                View profile
              </Button>
            ) : null}
          </div>
        </>
      }
      imageUrl={util.avatarUrl(user)}
      labels={user.iconRoles}
      actionIcon={actionIcon}
      actionClick={actionClick}
      actionTooltip={actionClick ? actionTooltip : undefined}
      footer={
        canGhost ? (
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", marginTop: 10 }}>
            <Button
              basic
              style={{ floated: "right" }}
              size="small"
              onClick={() => {
                document.dispatchEvent(new MouseEvent("click", { bubbles: true }))
                ghost()
              }}
            >
              <Icon name="snapchat ghost" />
              Ghost
            </Button>
          </div>
        ) : null
      }
      linkTo="#"
    />
  )
}

export default UserChip
